import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@/components/layout/AppShell';
import { PageContainer } from '@/components/layout/PageContainer';
import { TopBar } from '@/components/layout/TopBar';
import { Spinner } from '@/components/ui/Spinner';
import { Button } from '@/components/ui/Button';
import { BoardingPass } from '@/components/domain/BoardingPass';
import { useBookingDetails, useEtkUrl } from '@/api/queries/booking';
import { useUIStore } from '@/store/ui';
import { paths } from '@/routes/paths';

// Feature flag: ETK (e-ticket) download is a MVP-deferred capability
// (Addendum-01). Backend currently returns 501 for /bookings/{id}/etk.
// Hide the button until the backend implementation lands. Toggle via
// VITE_FEATURE_ETK_DOWNLOAD=true to re-enable for QA.
const ETK_DOWNLOAD_ENABLED = import.meta.env.VITE_FEATURE_ETK_DOWNLOAD === 'true';

export function BoardingPassPage(): JSX.Element {
  const { t } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();
  const booking = useBookingDetails(id);
  const navigate = useNavigate();
  const pushToast = useUIStore((s) => s.pushToast);
  const etk = useEtkUrl(id);

  const handleDownload = async () => {
    try {
      const r = await etk.refetch();
      if (r.data?.download_url) {
        window.open(r.data.download_url, '_blank', 'noopener');
      }
    } catch {
      pushToast({ body: t('errors.generic'), variant: 'error' });
    }
  };

  const showEtkButton = ETK_DOWNLOAD_ENABLED && booking.data?.etk_available === true;

  return (
    <AppShell>
      <PageContainer>
        <TopBar back title={t('booking.ticket_title')} bell />
        {booking.isLoading && <Spinner />}
        {booking.data && (
          <>
            <BoardingPass booking={booking.data} />
            {showEtkButton && (
              <Button block size="lg" variant="primary" onClick={handleDownload} loading={etk.isFetching}>
                {t('booking.download_etk')}
              </Button>
            )}
            <Button block variant="ghost" onClick={() => navigate(paths.boardingActions(id))}>
              {t('common.more_details')}
            </Button>
          </>
        )}
      </PageContainer>
    </AppShell>
  );
}
